import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications, type AppNotification } from "@/hooks/useNotifications";
import { notificationMeta } from "@/lib/notification";
import { formatRelativeTime } from "@/lib/format";

export default function NotificationBell() {
  const { notifications, loading, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const recent = notifications.slice(0, 6);

  const handleItem = (item: AppNotification) => {
    if (!item.is_read) void markRead(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
        aria-label="通知中心"
      >
        <i className="ri-notification-3-line text-lg" />
        {unreadCount > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-background-50">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50 sm:w-96">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="font-heading text-sm font-semibold text-foreground-950">通知</span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                  {unreadCount} 条未读
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="cursor-pointer text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
              >
                全部已读
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-xs text-foreground-500">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
                加载中…
              </div>
            ) : recent.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                  <i className="ri-notification-off-line text-lg" />
                </span>
                <p className="text-xs text-foreground-500">暂时没有新通知</p>
              </div>
            ) : (
              recent.map((item) => {
                const meta = notificationMeta(item.type);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItem(item)}
                    className={`flex w-full cursor-pointer items-start gap-3 border-b border-background-100 px-4 py-3 text-left transition-colors hover:bg-background-100 ${
                      item.is_read ? "" : "bg-primary-50/60"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${meta.chip}`}>
                      <i className={`${meta.icon} text-base`} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-xs font-medium text-foreground-950">{item.title}</span>
                        {!item.is_read && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />}
                      </span>
                      {item.body && (
                        <span className="mt-1 line-clamp-2 block text-[11px] leading-relaxed text-foreground-600">
                          {item.body}
                        </span>
                      )}
                      <span className="mt-1 block text-[10px] text-foreground-400">
                        {formatRelativeTime(item.created_at)}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-background-200 px-4 py-3 text-center text-xs font-medium text-primary-700 transition-colors hover:bg-background-100"
          >
            查看全部通知
          </Link>
        </div>
      )}
    </div>
  );
}